import {
  type CarouselProps,
  Carousel as MantineCarousel,
} from "@mantine/carousel";
import { ThemeIcon } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import Autoplay from "embla-carousel-autoplay";
import { type FC, type ReactNode, useMemo, useRef } from "react";

type FlattenCarouselProps = Omit<CarouselProps, "emblaOptions"> &
  Pick<NonNullable<CarouselProps["emblaOptions"]>, "loop">;

interface ICarouselProps extends FlattenCarouselProps {
  children?: ReactNode;
  className?: string;
  controlIconClassName?: string;
  autoPlay?: boolean;
  initialSlide?: number;
  slideSize?:
    | string
    | {
        base: string;
        xs?: string;
        sm?: string;
        md?: string;
        lg?: string;
        xl?: string;
      };
}

const SSCarousel: FC<ICarouselProps> = ({
  children,
  className,
  controlIconClassName,
  autoPlay = false,
  slideSize = {
    base: "100%",
    xs: "50%",
    sm: "50%",
    md: "50%",
    lg: "33%",
    xl: "25%",
  },
  initialSlide = 0,
  loop = true,
  ...rest
}) => {
  const autoplay = useRef(Autoplay({ delay: 4000 }));
  const options = autoPlay ? [autoplay.current] : [];

  const displaySlides = useMemo(
    () => (Array.isArray(children) ? children : [children]),
    [children],
  );

  return (
    <MantineCarousel
      {...rest}
      emblaOptions={{ loop, align: "center", slidesToScroll: 1 }}
      withIndicators
      className={`max-w-full ${className}`}
      classNames={{
        controls: "!z-[10]",
        viewport:
          "relative block" +
          " before:absolute before:w-20 before:h-full before:z-[1] before:hidden before:xs:block before:bg-gradient-to-r before:from-primary-background before:from-30% before:to-transparent " +
          " after:absolute after:top-0 after:right-0 after:w-16 after:h-full after:z-[1] after:hidden after:xs:block after:bg-gradient-to-l after:from-primary-background after:from-30% after:to-transparent ",
      }}
      controlSize={30}
      initialSlide={initialSlide}
      nextControlIcon={
        <ThemeIcon
          className={controlIconClassName ?? "default-icon"}
          radius={"xl"}
          size={"xl"}
          title="Avancer d'une slide"
          variant="transparent"
        >
          <IconChevronRight />
        </ThemeIcon>
      }
      plugins={options}
      previousControlIcon={
        <ThemeIcon
          className={controlIconClassName ?? "default-icon"}
          radius={"xl"}
          size={"xl"}
          title="Reculer d'une slide"
          variant="transparent"
        >
          <IconChevronLeft />
        </ThemeIcon>
      }
      slideGap="md"
      slideSize={slideSize}
      onMouseEnter={autoplay.current.stop}
      onMouseLeave={autoplay.current.reset}
    >
      {displaySlides.map((slide, index) => (
        <MantineCarousel.Slide
          key={index}
          className={"my-5 flex select-none items-center justify-center"}
        >
          {slide}
        </MantineCarousel.Slide>
      ))}
    </MantineCarousel>
  );
};

export default SSCarousel;
